import { useOutletContext } from 'react-router';
import type { MembershipSummary } from '../../../src/shared/auth';
import { useMe } from '../auth/useMe';
import { HealthBadge } from '../../components/HealthBadge';

/**
 * `/w/:slug` index — the Phase 1 workspace landing. Renders the REAL data the
 * Phase 1 API surface can serve (the `GET /api/auth/me` bootstrap + health):
 * workspace identity, the caller's role, and account details. Operational
 * widgets (missed calls, pipeline, usage meters — §10) arrive with their
 * backing endpoints in later phases and are not stubbed here.
 */
export function WorkspaceOverview(): React.JSX.Element {
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();
  const me = useMe();
  const userName = me.data?.user?.name;

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{membership.name}</h1>
      <p className="mt-1 text-sm text-slate-500">
        <code className="font-mono">/w/{membership.slug}</code>
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-medium text-slate-700">Workspace</h2>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-slate-500">Name</dt>
            <dd>{membership.name}</dd>
            <dt className="text-slate-500">Slug</dt>
            <dd>
              <code className="font-mono text-xs">{membership.slug}</code>
            </dd>
            <dt className="text-slate-500">Your role</dt>
            <dd className="capitalize">{membership.role}</dd>
            <dt className="text-slate-500">Workspace id</dt>
            <dd>
              <code className="font-mono text-xs">{membership.tenantId}</code>
            </dd>
          </dl>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-medium text-slate-700">Signed in as</h2>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-slate-500">Email</dt>
            <dd>{me.data?.user?.email}</dd>
            <dt className="text-slate-500">Name</dt>
            <dd>{userName !== undefined && userName !== '' ? userName : '—'}</dd>
          </dl>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:col-span-2">
          <h2 className="text-sm font-medium text-slate-700">Backend status</h2>
          <div className="mt-3">
            <HealthBadge />
          </div>
        </section>
      </div>
    </div>
  );
}
